import styled from '@emotion/styled';

import { useI18n } from '../../../hooks/useI18nFormatters';

import { animationFade } from '../../../theme/animations/animationFade';
import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { motion } from '../../../theme/motion';
import { theme } from '../../../theme/theme';

import { IconButton } from '../../../components/buttons/IconButton';
import { Heading1 } from '../../../components/common/text/Heading1';
import { Heading2Medium } from '../../../components/common/text/Heading2Medium';
import { Heading4 } from '../../../components/common/text/Heading4';
import { TextRegular } from '../../../components/common/text/TextRegular';

import { AdvancedFlowGraphic } from './AdvancedFlowGraphic';
import { StandardFlowGraphic } from './StandardFlowGraphic';

export function FlowTypeSelectView(props) {
  const { i18n } = useI18n();

  return (
    <FlowTypeSelectView.Root>
      <FlowTypeSelectView.CloseButton
        url={glyph.core.cross}
        color={theme.icon.color_light}
        hoverColor={theme.icon.color_light_hover}
        size={theme.icon.size_default}
        onPress={props.onCloseRequest}
      />

      <FlowTypeSelectView.Content>
        <Heading1>{i18n.messageFormatter('flow.kindOfFlowTitle')}</Heading1>
        <FlowTypeSelectView.CaptionLarge color={theme.color.text_light}>
          {i18n.messageFormatter('flow.kindOfFlowQuestion')}
        </FlowTypeSelectView.CaptionLarge>
        <FlowTypeSelectView.Buttons>
          {/* Standard Flow */}
          <FlowTypeSelectView.Button
            className="standard-flow-graphic-wrapper"
            onClick={props.onNavigateToFlowRequest}
          >
            <FlowTypeSelectView.ButtonInner>
              <Heading2Medium>{i18n.messageFormatter('flow.standardFlowTitle')}</Heading2Medium>
              <FlowTypeSelectView.Caption color={theme.color.text_light}>
                {i18n.messageFormatter('flow.standardFlowSubtitle')}
              </FlowTypeSelectView.Caption>
              <FlowTypeSelectView.Graphic>
                <StandardFlowGraphic />
              </FlowTypeSelectView.Graphic>
            </FlowTypeSelectView.ButtonInner>
          </FlowTypeSelectView.Button>

          {/* Advanced Flow */}
          <FlowTypeSelectView.Button
            className="standard-flow-graphic-wrapper"
            onClick={props.onNavigateToAdvancedFlowRequest}
          >
            <FlowTypeSelectView.ButtonInner>
              <Heading2Medium>Advanced Flow</Heading2Medium>
              <FlowTypeSelectView.Caption color={theme.color.text_light}>
                {i18n.messageFormatter('flow.advancedFlowSubtitle')}
              </FlowTypeSelectView.Caption>
              <FlowTypeSelectView.Graphic>
                <AdvancedFlowGraphic />
              </FlowTypeSelectView.Graphic>
            </FlowTypeSelectView.ButtonInner>
          </FlowTypeSelectView.Button>
        </FlowTypeSelectView.Buttons>
      </FlowTypeSelectView.Content>
    </FlowTypeSelectView.Root>
  );
}

FlowTypeSelectView.Root = styled.div`
  position: relative;
  text-align: center;
  animation: ${animationFade.in} ${motion.duration.normal} ease-out forwards;
`;

FlowTypeSelectView.CloseButton = styled(IconButton)`
  position: absolute;
  right: ${su(2)};
  top: ${su(2)};
`;

FlowTypeSelectView.Content = styled.div`
  padding: ${su(4, 4, 0, 4)};
`;

FlowTypeSelectView.CaptionLarge = styled(Heading4)`
  margin-top: 5px;
`;

FlowTypeSelectView.Caption = styled(TextRegular)`
  margin-top: 3px;
  padding: 0 5px;
`;

FlowTypeSelectView.Buttons = styled.div`
  display: grid;
  grid-gap: ${su(2)};
  grid-template-columns: 1fr 1fr;
  margin: ${su(4)} 0;
`;

FlowTypeSelectView.Button = styled.button`
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  font-weight: ${theme.fontWeight.regular};
`;

FlowTypeSelectView.ButtonInner = styled.div`
  display: block;
  width: 360px;
  height: 280px;
  border: 2px solid ${theme.color.line};
  transition: ${motion.duration.fast} ${motion.curve.fast_in};
  transition-property: transform, background-color, border;
  border-radius: ${theme.borderRadius.default};
  padding: ${su(3)} 0;
  background: ${theme.color.mono_010};
  cursor: pointer;
  overflow: hidden; // cut off graphic

  ${FlowTypeSelectView.Button}:hover & {
    background-color: ${theme.color.blue_050};
    border-color: ${theme.color.highlight_hover};
  }

  ${FlowTypeSelectView.Button}:hover:active & {
    border-color: ${theme.color.highlight_active};
    transform: scale(0.98);
  }
`;

FlowTypeSelectView.Graphic = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin-inline-start: ${su(-2)};
  margin-inline-end: ${su(-2)};
  transform: translate3d(0, 0, 0);
`;
